package learning.components

import antd.button.button
import antd.card.card
import antd.divider.divider
import antd.form.form
import antd.form.formItem
import antd.icon.deleteOutlined
import antd.icon.editOutlined
import antd.icon.plusOutlined
import antd.input.input
import antd.message.message
import antd.modal.modal
import antd.popconfirm.popconfirm
import antd.skeleton.skeleton
import com.jingfangpai.common.data.type.DateTime
import com.jingfangpai.common.orm.mapping.Comment
import com.jingfangpai.common.orm.mapping.enums.CommentEntityType
import com.jingfangpai.common.orm.mapping.enums.CommentStatus
import com.jingfangpai.common.orm.mapping.enums.CommentType
import com.jingfangpai.content.model.ContentQuery
import com.jingfangpai.content.model.ContentVo
import external.ckEditor.CKEditor
import external.ckEditor.ClassicEditor
import external.ckEditor.EditorConf
import external.ckEditor.EditorToolbarConf
import kotlinx.browser.document
import kotlinext.js.Object
import kotlinext.js.js
import kotlinext.js.jsObject
import kotlinx.html.style
import org.w3c.dom.HTMLScriptElement
import react.*
import react.dom.div
import react.dom.jsStyle
import react.dom.span
import learning.containers.ShanghanlunDetailPageContext
import react.dom.h3
import user.service.AuthService

external interface ShanghanlunDetailProps : RProps {
    var contentId: String
}

val ShanghanlunDetail = functionalComponent<ShanghanlunDetailProps> { props ->

    val ctx = useContext(ShanghanlunDetailPageContext)
    val notesCtx = useContext(NotesSectionContext)
    val (content, setContent) = useState<ContentVo?>(null)
    val (isLoading, setIsLoading) = useState(true)

    // Annotation editor state
    val (showAnnotationModal, setShowAnnotationModal) = useState(false)
    val (editingAnnotation, setEditingAnnotation) = useState<Comment?>(null)
    val (annotationForm, setAnnotationForm) = useState(Comment())
    val (saving, setSaving) = useState(false)
    val (saveError, setSaveError) = useState<String?>(null)
    val (annotationEditor, setAnnotationEditor) = useState<CKEditor?>(null)
    val (editorScriptLoaded, setEditorScriptLoaded) = useState(false)

    // CKEditor Base64 image upload adapter
    val configureBase64Upload = js("""
function(editor) {
    editor.plugins.get('FileRepository').createUploadAdapter = function(loader) {
        return {
            upload: function() {
                return loader.file.then(function(file) {
                    return new Promise(function(resolve) {
                        var reader = new FileReader();
                        reader.onload = function() { resolve({ 'default': reader.result }); };
                        reader.readAsDataURL(file);
                    });
                });
            }
        };
    };
}
""").unsafeCast<(dynamic) -> Unit>()

    // Load CKEditor script once (global singleton)
    useEffectOnce {
        if (js("typeof ClassicEditor !== 'undefined'").unsafeCast<Boolean>()) {
            setEditorScriptLoaded(true)
        } else if (document.getElementById("ckeditor-script-global") != null) {
            setEditorScriptLoaded(true)
        } else {
            val s = document.createElement("script") as HTMLScriptElement
            s.src = "/ckeditor.js"
            s.id = "ckeditor-script-global"
            document.body?.appendChild(s)
            s.onload = {
                setEditorScriptLoaded(true)
            }
        }
    }

    // Initialize / cleanup editor when annotation modal visibility changes
    useEffect(showAnnotationModal, editorScriptLoaded) {
        if (showAnnotationModal && editorScriptLoaded) {
            js("setTimeout")({
                val el = document.querySelector("#annotation-editor-detail")
                if (el != null) {
                    val config = jsObject<EditorConf> {
                        toolbar = jsObject<EditorToolbarConf> {
                            shouldNotGroupWhenFull = true
                            items = arrayOf(
                                "bold", "italic", "underline",
                                "fontSize", "fontColor", "link",
                                "bulletedList", "numberedList",
                                "|",
                                "outdent", "indent",
                                "|",
                                "imageUpload", "blockQuote",
                                "undo", "redo"
                            )
                        }
                        language = "zh-cn"
                        licenseKey = ""
                    }
                    ClassicEditor.create(el, config)
                        .then { editor: dynamic ->
                            configureBase64Upload(editor)
                            setAnnotationEditor(editor.unsafeCast<CKEditor>())
                            annotationForm.comment?.let { data ->
                                editor.setData(data)
                            }
                            editor.model.document.on("change:data") { _: dynamic, _: dynamic ->
                                val newComment = editor.getData() as String
                                setAnnotationForm { current ->
                                    Object.assign(Comment(), current).apply {
                                        comment = newComment
                                    }
                                }
                            }
                        }
                        .catch { error: dynamic ->
                            console.error("CKEditor init failed:", error)
                        }
                }
            }, 150)
        }
    }

    fun destroyAnnotationEditor() {
        annotationEditor?.let { editor ->
            try {
                editor.asDynamic().destroy()
            } catch (e: dynamic) {
                console.log("Editor destroy error:", e)
            }
        }
        setAnnotationEditor(null)
        val el = document.querySelector("#annotation-editor-detail")
        if (el != null) {
            el.innerHTML = ""
        }
    }

    fun loadContentDetail() {
        val query = ContentQuery().apply {
            id = props.contentId
            includeComments = true
        }

        ctx.query(query, { resp ->
            if (resp.ok() && resp.data != null && resp.data!!.isNotEmpty()) {
                setContent(resp.data!![0])
                setIsLoading(false)
            } else {
                message.error("数据为空")
                setIsLoading(false)
            }
        }, { error ->
            console.log(error)
            message.error("获取内容出错：${error.message}")
            setIsLoading(false)
        })
    }

    useEffectOnce {
        loadContentDetail()
    }

    fun openAnnotationModal(comment: Comment? = null) {
        setEditingAnnotation(comment)
        setSaveError(null)
        setAnnotationForm(comment ?: Comment())
        setShowAnnotationModal(true)
    }

    fun handleSaveAnnotation() {
        val editorComment = annotationEditor?.asDynamic()?.getData() as? String
        val commentToSave = editorComment ?: annotationForm.comment

        if (commentToSave.isNullOrBlank()) {
            setSaveError("请输入注解内容")
            return
        }

        setSaving(true)
        setSaveError(null)
        destroyAnnotationEditor()

        val toSave = Object.assign(Comment(), annotationForm).apply {
            comment = commentToSave
            entityType = CommentEntityType.Content
            entityId = props.contentId
            type = CommentType.注解
            createdBy = AuthService.getUserAuthData()?.id
            status = CommentStatus.正常
        }
        if (toSave.createdOn == null) {
            toSave.createdOn = DateTime()
        }

        notesCtx?.saveComment?.invoke(
            toSave,
            { resp ->
                if (resp.ok()) {
                    message.success("保存成功")
                    setShowAnnotationModal(false)
                    setEditingAnnotation(null)
                    setAnnotationForm(Comment())
                    loadContentDetail()
                } else {
                    message.error("保存失败：${resp.message}")
                }
                setSaving(false)
            },
            { error ->
                message.error("保存失败：${error.message}")
                setSaving(false)
            }
        )
    }

    fun handleDeleteAnnotation(comment: Comment) {
        val toDelete = Object.assign(Comment(), comment).apply {
            status = CommentStatus.已删除
        }
        notesCtx?.saveComment?.invoke(
            toDelete,
            { resp ->
                if (resp.ok()) {
                    message.success("删除成功")
                    loadContentDetail()
                } else {
                    message.error("删除失败：${resp.message}")
                }
            },
            { error ->
                message.error("删除失败：${error.message}")
            }
        )
    }

    div("ShanghanlunDetail") {
        if (isLoading) {
            skeleton {
                attrs {
                    active = true
                    loading = true
                }
            }
        } else {
            content?.let { currentContent ->
                // Clause number and title header
                val clauseHeader = buildString {
                    currentContent.index?.let { index ->
                        append("第${index}条")
                    }
                    if (!currentContent.title.isNullOrBlank()) {
                        if (isNotEmpty()) append(" · ")
                        append(currentContent.title)
                    }
                }

                // Content Section
                card {
                    val cardAttrs = attrs
                    cardAttrs.title = buildElement {
                        span {
                            if (clauseHeader.isNotBlank()) {
                                +clauseHeader
                            } else {
                                +"条文内容"
                            }
                        }
                    }
                    cardAttrs.style = js {
                        marginBottom = "16px"
                    }
                    h3 {
                        attrs.jsStyle = js {
                            lineHeight = "1.8"
                            fontSize = "15px"
                            fontWeight="bold"
                        }
                        attrs["dangerouslySetInnerHTML"] = js { __html = currentContent.content ?: "" }
                    }
                }

                // Annotations Section (Comments)
                card {
                    val cardAttrs = attrs
                    cardAttrs.title = buildElement {
                        span {
                            +"注解 (${currentContent.comments.size})"
                        }
                    }
                    cardAttrs.extra = buildElement {
                        button {
                            attrs {
                                type = "primary"
                                size = "small"
                                icon = buildElement { plusOutlined {} }
                                onClick = { openAnnotationModal() }
                            }
                            +"添加注解"
                        }
                    }
                    cardAttrs.style = js {
                        marginBottom = "16px"
                    }
                    if (currentContent.comments.isEmpty()) {
                        div {
                            attrs.jsStyle = js {
                                textAlign = "center"
                                padding = "20px"
                                color = "#999"
                            }
                            +"暂无注解"
                        }
                    } else {
                        currentContent.comments.forEachIndexed { index, comment ->
                            child(AnnotationItem) {
                                attrs {
                                    this.comment = comment
                                    onEdit = { openAnnotationModal(comment) }
                                    onDelete = { handleDeleteAnnotation(comment) }
                                }
                            }
                            if (index < currentContent.comments.size - 1) {
                                divider {}
                            }
                        }
                    }
                }

                // Annotation Edit Modal
                modal {
                    attrs {
                        title = if (editingAnnotation != null) "编辑注解" else "添加注解"
                        visible = showAnnotationModal
                        maskClosable = false
                        onOk = { handleSaveAnnotation() }
                        onCancel = {
                            destroyAnnotationEditor()
                            setSaveError(null)
                            setShowAnnotationModal(false)
                            setEditingAnnotation(null)
                            setAnnotationForm(Comment())
                        }
                        okText = if (saving) "保存中..." else "保存"
                        cancelText = "取消"
                        confirmLoading = saving
                        width = 700
                    }
                    div {
                        // 显示对应条文内容（只读）
                        val articleText = content?.content
                        if (!articleText.isNullOrBlank()) {
                            div {
                                attrs.jsStyle = js {
                                    marginBottom = "16px"
                                    padding = "12px"
                                    backgroundColor = "#fafafa"
                                    borderRadius = "4px"
                                    borderLeft = "3px solid #1890ff"
                                    fontSize = "14px"
                                    lineHeight = "1.8"
                                    color = "#333"
                                    maxHeight = "150px"
                                    overflowY = "auto"
                                }
                                div {
                                    attrs.jsStyle = js {
                                        fontSize = "12px"
                                        color = "#999"
                                        marginBottom = "6px"
                                    }
                                    +"条文原文${content?.index?.let { "（第${it}条）" } ?: ""}"
                                }
                                div {
                                    attrs["dangerouslySetInnerHTML"] = js { __html = articleText }
                                }
                            }
                        }
                        form {
                            formItem {
                                attrs { label = "标题" }
                                input {
                                    attrs {
                                        value = annotationForm.title ?: ""
                                        placeholder = "请输入注解标题（可选）"
                                        disabled = saving
                                        onChange = { event: dynamic ->
                                            val newTitle = event.target.value as String
                                            setAnnotationForm { current ->
                                                Object.assign(Comment(), current).apply {
                                                    title = newTitle
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                            formItem {
                                attrs { label = "注解内容" }
                                div {
                                    attrs.jsStyle = js {
                                        border = "1px solid #d9d9d9"
                                        borderRadius = "4px"
                                        minHeight = "300px"
                                    }
                                    attrs["id"] = "annotation-editor-detail"
                                }
                                if (!saveError.isNullOrBlank()) {
                                    div {
                                        attrs.jsStyle = js {
                                            color = "#ff4d4f"
                                            fontSize = "12px"
                                            marginTop = "4px"
                                        }
                                        +saveError!!
                                    }
                                }
                            }
                            formItem {
                                attrs { label = "来源" }
                                input {
                                    attrs {
                                        value = annotationForm.source ?: ""
                                        placeholder = "请填写注解来源（可选）"
                                        disabled = saving
                                        onChange = { event: dynamic ->
                                            val newSource = event.target.value as String
                                            setAnnotationForm { current ->
                                                Object.assign(Comment(), current).apply {
                                                    source = newSource
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }

                // Notes Section
                child(NotesSection) {
                    attrs {
                        entityType = CommentEntityType.Content
                        entityId = props.contentId
                    }
                }
            }
        }
    }
}

external interface AnnotationItemProps : RProps {
    var comment: Comment
    var onEdit: () -> Unit
    var onDelete: () -> Unit
}

val AnnotationItem = functionalComponent<AnnotationItemProps> { props ->
    div("AnnotationItem") {
        // Comment header with type, author and actions
        div {
            attrs.jsStyle = js {
                display = "flex"
                justifyContent = "space-between"
                alignItems = "center"
                marginBottom = "10px"
            }
            
            div {
                props.comment.type?.let { type ->
                    span {
                        attrs.jsStyle = js {
                            backgroundColor = "#e6f7ff"
                            color = "#1890ff"
                            padding = "2px 8px"
                            borderRadius = "4px"
                            fontSize = "12px"
                            marginRight = "8px"
                        }
                        +type.name
                    }
                }

                props.comment.createdBy?.let { author ->
                    span {
                        attrs.jsStyle = js {
                            color = "#999"
                            fontSize = "12px"
                        }
                        +author
                    }
                }
            }

            div {
                button {
                    attrs {
                        type = "link"
                        size = "small"
                        icon = buildElement { editOutlined {} }
                        onClick = { props.onEdit() }
                    }
                    +"编辑"
                }
                popconfirm {
                    attrs {
                        title = "确定要删除这条注解吗？"
                        onConfirm = { props.onDelete() }
                        okText = "删除"
                        cancelText = "取消"
                    }
                    button {
                        attrs {
                            type = "link"
                            size = "small"
                            icon = buildElement { deleteOutlined {} }
                        }
                        +"删除"
                    }
                }
            }
        }

        // Title (if exists)
        if (!props.comment.title.isNullOrBlank()) {
            div {
                attrs.jsStyle = js {
                    fontWeight = "bold"
                    fontSize = "15px"
                    marginBottom = "8px"
                    color = "#333"
                }
                +props.comment.title!!
            }
        }

        // Comment content (rendered as HTML)
        props.comment.comment?.let { htmlContent ->
            div {
                attrs.jsStyle = js {
                    lineHeight = "1.6"
                    fontSize = "14px"
                    marginBottom = if (!props.comment.source.isNullOrBlank()) "4px" else "0px"
                }
                attrs["dangerouslySetInnerHTML"] = js { __html = htmlContent }
            }
        }

        // Source (if exists)
        if (!props.comment.source.isNullOrBlank()) {
            div {
                attrs.jsStyle = js {
                    fontSize = "12px"
                    color = "#999"
                    marginTop = "4px"
                }
                +"来源：${props.comment.source}"
            }
        }
    }
}
